import { useState, useCallback, useMemo } from "react";
import {
  reports,
  recentReports,
  reportTemplates,
  reportsSummary,
} from "@/mocks/relatorios";
import type { ReportItem, ReportTemplate } from "@/mocks/relatorios";
import ReportsKpiCard from "./components/ReportsKpiCard";
import ReportsFilters from "./components/ReportsFilters";
import ReportsTable from "./components/ReportsTable";
import ReportCard from "./components/ReportCard";
import ReportPreviewDrawer from "./components/ReportPreviewDrawer";
import ReportGeneratorWizard from "./components/ReportGeneratorWizard";
import ReportTemplatesGrid from "./components/ReportTemplatesGrid";
import CtaReportsPanel from "./components/CtaReportsPanel";
import ProjectReportsPanel from "./components/ProjectReportsPanel";
import EnvironmentalReportsPanel from "./components/EnvironmentalReportsPanel";
import CarbonReportsPanel from "./components/CarbonReportsPanel";
import DocumentaryRrrReportsPanel from "./components/DocumentaryRrrReportsPanel";
import GisExportsPanel from "./components/GisExportsPanel";
import RecentReportsPanel from "./components/RecentReportsPanel";
import ReportPendingPanel from "./components/ReportPendingPanel";

const tabs = [
  { id: "biblioteca", label: "Biblioteca de Relatórios" },
  { id: "gerador", label: "Gerador de Relatórios" },
  { id: "modelos", label: "Modelos" },
  { id: "cta", label: "CTA Arbóreo" },
  { id: "projetos", label: "Projetos" },
  { id: "ambiental", label: "Ambiental" },
  { id: "carbono", label: "Carbono" },
  { id: "documental", label: "Documental e RRR" },
  { id: "gis", label: "Exportações GIS" },
];

const initialFilters = {
  search: "",
  type: "",
  project: "",
  municipality: "",
  status: "",
  period: "",
  format: "",
  responsible: "",
  isPublic: "",
};

export default function Relatorios() {
  const [activeTab, setActiveTab] = useState("biblioteca");
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [filters, setFilters] = useState(initialFilters);

  const handleView = useCallback((r: ReportItem) => setSelectedReport(r), []);
  const handleCloseDrawer = useCallback(() => setSelectedReport(null), []);
  const handleOpenWizard = useCallback(() => setShowWizard(true), []);
  const handleCloseWizard = useCallback(() => setShowWizard(false), []);
  const handleUseTemplate = useCallback((_tpl: ReportTemplate) => {
    setShowWizard(true);
  }, []);

  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!r.title.toLowerCase().includes(s) && !r.projectName.toLowerCase().includes(s) && !r.responsible.toLowerCase().includes(s)) return false;
      }
      if (filters.type && r.type !== filters.type) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (filters.format && r.format !== filters.format) return false;
      if (filters.municipality && !r.municipality.toLowerCase().includes(filters.municipality.toLowerCase())) return false;
      if (filters.isPublic === "public" && !r.isPublic) return false;
      if (filters.isPublic === "private" && r.isPublic) return false;
      if (filters.responsible && !r.responsible.toLowerCase().includes(filters.responsible.toLowerCase())) return false;
      return true;
    });
  }, [filters]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Relatórios</div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            Relatórios
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Gere relatórios técnicos, executivos, cartográficos, fotográficos e ambientais a partir dos dados do CTA, projetos, áreas verdes, carbono e governança.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleOpenWizard}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Novo relatório
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
            <i className="ri-file-list-3-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Gerar relatório técnico
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-government-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Gerar relatório executivo
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Exportar histórico
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-file-copy-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Modelos de relatório
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <ReportsKpiCard label="Relatórios gerados" value={String(reportsSummary.totalGenerated)} icon="ri-article-line" color="primary" subtitle="Total no repositório" />
        <ReportsKpiCard label="Relatórios técnicos" value={String(reportsSummary.technicalReports)} icon="ri-file-list-3-line" color="primary" subtitle="Inventários e pareceres" />
        <ReportsKpiCard label="Relatórios fotográficos" value={String(reportsSummary.photoReports)} icon="ri-camera-line" color="accent" subtitle="Com imagens georreferenciadas" />
        <ReportsKpiCard label="Relatórios GIS" value={String(reportsSummary.gisReports)} icon="ri-map-2-line" color="accent" subtitle="Exportações geoespaciais" />
        <ReportsKpiCard label="Em processamento" value={String(reportsSummary.inProgress)} icon="ri-loader-4-line" color="warning" subtitle="Gerando no momento" />
        <ReportsKpiCard label="Pendentes de revisão" value={String(reportsSummary.pendingReview)} icon="ri-search-eye-line" color="warning" subtitle="Aguardando aprovação" />
        <ReportsKpiCard label="Com erro" value={String(reportsSummary.withError)} icon="ri-error-warning-line" color="danger" subtitle="Falha na geração" />
        <ReportsKpiCard label="Modelos disponíveis" value={String(reportsSummary.availableTemplates)} icon="ri-stack-line" color="secondary" subtitle="Templates ativos" />
      </div>

      {/* Quick access panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RecentReportsPanel reports={recentReports} onView={handleView} />
        <ReportPendingPanel />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Biblioteca */}
        {activeTab === "biblioteca" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Biblioteca de relatórios</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCards(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Tabela
                </button>
                <button
                  onClick={() => setShowCards(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Cards
                </button>
              </div>
            </div>

            <ReportsFilters filters={filters} onChange={setFilters} />

            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-filter-line w-4 h-4 flex items-center justify-center" />
              <span>{filteredReports.length} de {reports.length} relatórios encontrados</span>
            </div>

            {showCards ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredReports.map((r) => (
                  <ReportCard key={r.id} report={r} onView={handleView} />
                ))}
              </div>
            ) : (
              <ReportsTable reports={filteredReports} onView={handleView} />
            )}

            {filteredReports.length === 0 && (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-file-search-line text-4xl mb-3 block" />
                <p className="text-sm">Nenhum relatório encontrado com os filtros atuais.</p>
              </div>
            )}
          </div>
        )}

        {/* Gerador de Relatórios */}
        {activeTab === "gerador" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Assistente de geração de relatórios</h3>
            </div>
            <div className="bg-white rounded-xl border border-background-200/70 p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-magic-line text-primary-500 w-full h-full flex items-center justify-center text-2xl" />
              </div>
              <h4 className="text-base font-semibold text-foreground-900 mb-2">Gerar um novo relatório</h4>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-4">
                O assistente irá guiar você em 6 etapas: tipo, escopo, filtros, conteúdo, formato e revisão.
              </p>
              <button
                onClick={handleOpenWizard}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                Iniciar assistente
              </button>
            </div>
          </div>
        )}

        {/* Modelos */}
        {activeTab === "modelos" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900">Modelos de relatório</h3>
            <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 mb-2">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-secondary-600 w-full h-full flex items-center justify-center text-lg" />
                </div>
                <p className="text-xs text-secondary-700 leading-relaxed">
                  Os modelos definem a estrutura padrão dos relatórios. Cada modelo pode ser personalizado por projeto ou tipo de saída.
                  Modelos inativos não aparecem como opção no gerador de relatórios.
                </p>
              </div>
            </div>
            <ReportTemplatesGrid onUseTemplate={handleUseTemplate} />
          </div>
        )}

        {/* CTA Arbóreo */}
        {activeTab === "cta" && <CtaReportsPanel />}

        {/* Projetos */}
        {activeTab === "projetos" && <ProjectReportsPanel />}

        {/* Ambiental */}
        {activeTab === "ambiental" && <EnvironmentalReportsPanel />}

        {/* Carbono */}
        {activeTab === "carbono" && <CarbonReportsPanel />}

        {/* Documental e RRR */}
        {activeTab === "documental" && <DocumentaryRrrReportsPanel />}

        {/* Exportações GIS */}
        {activeTab === "gis" && <GisExportsPanel />}
      </div>

      {/* Preview Drawer */}
      {selectedReport && (
        <ReportPreviewDrawer report={selectedReport} onClose={handleCloseDrawer} />
      )}

      {/* Generator Wizard Modal */}
      {showWizard && (
        <ReportGeneratorWizard onClose={handleCloseWizard} />
      )}
    </div>
  );
}